import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { EmpresaCadastralResponse } from '@interfaces/corporate-registry/responses/empresa-cadastral.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import {
  formatarCep,
  formatarCnae,
  formatarEndereco,
  formatarExterior,
  formatarPeriodoSimples,
  formatarTelefone,
  formatarTelefones,
  motivoRelevante,
  situacaoAtiva,
} from '@shared/components/ownership-network/registry-formatters';

interface CampoCadastro {
  rotulo: string;
  valor: string | null;
}

/**
 * "Cadastro (Receita Federal)" — mesmos campos, ordem e rótulos do Vault-Dossiê.
 *
 * ⚠️ Datas: a API manda meia-noite UTC (`2024-05-20T00:00:00Z`) para um dado que é só data. O
 * Dossiê formata isso no fuso local e mostra o dia ANTERIOR (19/05); aqui o `truncateTime` do
 * `DateFormatterService` mantém a data da Receita (20/05). Divergência de propósito, registrada
 * na comparação do card 3001.
 */
@Component({
  selector: 'app-registry-company-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './registry-company-card.component.html',
  styleUrl: './registry-company-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistryCompanyCardComponent {
  readonly empresa = input.required<EmpresaCadastralResponse>();

  /** Acordeão: começa aberto; o título recolhe e expande (pedido de 25/09/2026, card 2986). */
  protected readonly aberto = signal(true);

  /** Formatação de moeda do projeto ("R$ 1.234,56", igual ao Dossiê). */
  private readonly moeda = new VaultFormatCurrencyPipe();

  constructor(private readonly dateFormatter: DateFormatterService) {}

  private data(iso: string | null): string | null {
    return iso ? this.dateFormatter.formatDateInstant(iso, true) : null;
  }

  protected readonly ativa = computed(() => situacaoAtiva(this.empresa().situacaoCadastral));
  protected readonly motivo = computed(() => motivoRelevante(this.empresa().motivoSituacao));

  protected readonly campos = computed<CampoCadastro[]>(() => {
    const e = this.empresa();
    const fmt = (iso: string) => this.data(iso) ?? iso;
    const capital =
      e.capitalSocial === null || e.capitalSocial === undefined
        ? null
        : this.moeda.transform(e.capitalSocial, 2, 'R$');
    const situacao = e.situacaoCadastral
      ? `${e.situacaoCadastral}${e.dataSituacao ? ` desde ${fmt(e.dataSituacao)}` : ''}`
      : null;
    const exterior = formatarExterior(e.endereco);

    const campos: (CampoCadastro | null)[] = [
      { rotulo: 'Natureza jurídica', valor: e.naturezaJuridica },
      { rotulo: 'Qualificação do responsável', valor: e.qualificacaoResponsavel },
      { rotulo: 'CNAE principal', valor: formatarCnae(e.cnaePrincipal) },
      { rotulo: 'Início da atividade', valor: this.data(e.dataInicioAtividade) },
      { rotulo: 'Capital social', valor: capital },
      { rotulo: 'Situação cadastral', valor: situacao },
      this.motivo() ? { rotulo: 'Motivo da situação', valor: this.motivo() } : null,
      e.situacaoEspecial
        ? {
            rotulo: 'Situação especial',
            valor: e.dataSituacaoEspecial
              ? `${e.situacaoEspecial} (${fmt(e.dataSituacaoEspecial)})`
              : e.situacaoEspecial,
          }
        : null,
      {
        rotulo: 'Simples Nacional',
        valor: formatarPeriodoSimples(
          e.simples?.optante ?? false,
          e.simples?.dataOpcaoSimples ?? null,
          e.simples?.dataExclusaoSimples ?? null,
          fmt
        ),
      },
      {
        rotulo: 'MEI',
        valor: formatarPeriodoSimples(
          e.simples?.mei ?? false,
          e.simples?.dataOpcaoMei ?? null,
          e.simples?.dataExclusaoMei ?? null,
          fmt
        ),
      },
      { rotulo: 'Endereço', valor: e.endereco ? formatarEndereco(e.endereco) : null },
      { rotulo: 'CEP', valor: formatarCep(e.endereco?.cep ?? null) },
      exterior ? { rotulo: 'Exterior', valor: exterior } : null,
      { rotulo: 'Telefones', valor: formatarTelefones(e.telefones ?? []) },
      { rotulo: 'Fax', valor: formatarTelefone(e.fax) },
      { rotulo: 'E-mail', valor: e.email },
      e.enteFederativo ? { rotulo: 'Ente federativo', valor: e.enteFederativo } : null,
    ];
    return campos.filter((c): c is CampoCadastro => !!c);
  });

  protected readonly cnaesSecundarios = computed(() =>
    (this.empresa().cnaesSecundarios ?? []).map(c => formatarCnae(c) ?? c.codigo)
  );
}
